import { SortingState } from '@tanstack/react-table'
import { MagnifyingGlassIcon, ToothIcon } from '@phosphor-icons/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SimpleTable from 'app/components/table/SimpleTable'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { defaultColumns } from 'app/table-columns/treatment'
import { useCdtCodeMappings } from 'app/hooks/useCdtCodeMappings'
import { useHandleSearchParams } from 'app/utils'
import { BandSort, SORT_COLUMN, nextSort } from './treatmentSort'
import { SetStage } from './treatmentBulkActions'
import { useTreatmentBulkActions } from './useTreatmentBulkActions'
import { useLocationsInfo } from './useLocationNames'
import type { TreatmentDestination } from 'app/types/treatment'
import type { TreatmentRow } from './useTreatmentBoard'

/**
 * How close to the bottom a scroll has to get before the next page is asked
 * for. Roughly four rows of runway, so the rows arrive while there is still
 * something to read rather than under a spinner at the end.
 */
const SCROLL_MARGIN = 200

export type TreatmentTableProps = {
  rows: TreatmentRow[]
  /** Undefined until the first page lands, which is what draws the spinner. */
  ready: boolean
  loading: boolean
  hasMore: boolean
  loadMore: () => void
  sort: BandSort
  onSort: (next: BandSort) => void
  onOpenCase: (row: TreatmentRow) => void
  onOpenRecord: (row: TreatmentRow) => void
  /**
   * The board's own stage write, which both of this table's doors to it use:
   * a row's stage dropdown, and the bulk bar moving a whole selection at once.
   */
  setStage: SetStage
  /** The destinations the stage cell and the bulk bar offer; the old pipeline's when absent. */
  stages?: TreatmentDestination[]
  /** The booked stage: a row in it shows it read-only, and the bulk bar never offers it. */
  lockedStage?: TreatmentDestination
  /**
   * The search, location and band controls, rendered in the table's own
   * toolbar rather than up in the page header.
   *
   * In the board view those controls belong to the page: they narrow a board
   * that fills it. Here they belong to the table, sitting on its left edge in
   * line with the columns and opposite its own column controls, which is
   * where every other table in the app puts them.
   */
  controls?: React.ReactNode
  /**
   * The board/table switch, rendered at the right of the table's toolbar
   * beside the row-lines and column controls. Those three are the switches on
   * this page that change how the cases are drawn and nothing about which
   * cases they are, so they belong together.
   */
  rightControls?: React.ReactElement
  onVisibleColumnsChange?: (columnIds: string[]) => void
  /** False on a page that is not one band, so the empty states stop saying "in this band". */
  banded?: boolean
}

/**
 * The unscheduled-treatment band as a table.
 *
 * The same cases, the same stages and the same writes as the board next door:
 * this is a second reading of one queue, not a second queue. Where the board
 * is for holding a handful of conversations in flight, the table is for
 * scanning the whole band, sorting it by money or by recency, and moving a
 * row's stage without opening anything.
 */
export function TreatmentTable({
  rows,
  ready,
  loading,
  hasMore,
  loadMore,
  sort,
  onSort,
  onOpenCase,
  onOpenRecord,
  setStage,
  stages,
  lockedStage,
  controls,
  rightControls,
  onVisibleColumnsChange,
  banded = true,
}: TreatmentTableProps) {
  const locationNames = useLocationsInfo()
  const { categoryOf } = useCdtCodeMappings()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  // Built here rather than a floor up, so the pipelines and workflows the
  // action menu offers are only read when somebody is actually looking at the
  // table. The board next door has no bar and asks for none of it.
  const bulkActions = useTreatmentBulkActions(setStage, stages)

  // The row goes along as the known card: this table can hold a case from
  // outside the page of the band the board has loaded.
  const onSetStage = useCallback((row: TreatmentRow, stage: string) => setStage(row.id, stage, row), [setStage])

  // This table's search box and location filter are the board's, written to
  // `query` and `filters` by the header above it. The table shell knows
  // nothing about either, so `enableDeepLinking={false}` below stops it
  // guessing at a narrowed state from a `filters` param it does not own, and
  // the empty state is decided here instead.
  const query = searchParams.get(`query`) ?? ``
  const narrowed = !!query || !!searchParams.get(`filters`)

  const columns = useMemo(
    () => defaultColumns({ onOpenRecord, onSetStage, locationNames, stages, lockedStage, categoryOf }),
    [onOpenRecord, onSetStage, locationNames, stages, lockedStage, categoryOf],
  )

  const meta = useMemo(() => ({ onRowClick: onOpenCase }), [onOpenCase])

  // The shell owns which columns show and in what order; mirroring its changes lets a page
  // export what is on screen. Until the first change, each column's own default holds.
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>()
  const [columnOrder, setColumnOrder] = useState<string[]>()
  useEffect(() => {
    if (!onVisibleColumnsChange) return
    const defaults = new Map(columns.map((column) => [column.id as string, !!column.meta?.defaultVisible]))
    const order = columnOrder ?? [...defaults.keys()]
    onVisibleColumnsChange(order.filter((id) => columnVisibility?.[id] ?? defaults.get(id) ?? false))
  }, [onVisibleColumnsChange, columns, columnVisibility, columnOrder])

  const where = banded ? ` in this band` : ``

  // Which header wears the arrow. `sortOrder` in the table shell is derived
  // from this alone, so the arrow and the query can never disagree.
  const syncedSort = useMemo<SortingState>(
    () => [{ id: SORT_COLUMN[sort.sort], desc: sort.direction === `DESC` }],
    [sort],
  )

  const handleSortChange = useCallback((state: SortingState) => onSort(nextSort(sort, state)), [onSort, sort])

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || loading) return
      const el = event.currentTarget
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_MARGIN) loadMore()
    },
    [hasMore, loading, loadMore],
  )

  return (
    <SimpleTable<TreatmentRow>
      // Undefined rather than an empty array on the first read: an empty array
      // is a band with nobody in it, and the two must not look alike.
      data={ready ? rows : undefined}
      columns={columns}
      meta={meta}
      activeSubpage="treatment"
      showColumnOrdering
      hideFooter
      isManuallySorted
      syncedSort={syncedSort}
      onSortChange={handleSortChange}
      onColumnVisibilityChange={onVisibleColumnsChange && setColumnVisibility}
      onColumnOrderChange={onVisibleColumnsChange && setColumnOrder}
      onScroll={onScroll}
      enableDeepLinking={false}
      bulkActions={bulkActions}
      extraSlot={<div className="flex flex-wrap items-center gap-2">{controls}</div>}
      // `order-last` rather than a slot of its own: the shell renders this
      // group before the row-lines and column controls, and the switch has to
      // finish the row so its right edge lands exactly where the board view's
      // does. Anything else and it shifts under the cursor that just used it.
      extraRightSlot={<div className="order-last flex items-center">{rightControls}</div>}
      emptyState={
        narrowed ? (
          <TableEmptyState
            icon={<MagnifyingGlassIcon className="h-6 w-6" />}
            title="No matches"
            description={
              query
                ? `Nobody matching "${query}" has treatment outstanding${where}.`
                : `Nobody at the chosen locations has treatment outstanding${where}.`
            }
            action={
              <Button variant="secondary" size="sm" onClick={() => handleSearchParams({ query: ``, filters: `` })}>
                Clear search and filters
              </Button>
            }
          />
        ) : (
          <TableEmptyState
            icon={<ToothIcon className="h-6 w-6" />}
            title="Nothing outstanding"
            description={`Nobody${where} is waiting with treatment that still needs a time on the schedule.`}
          />
        )
      }
    />
  )
}
